import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useDataCache } from '../DataCacheContext';
import { API_BASE } from '../api';
import TimePeriodFilter from './TimePeriodFilter';

const RED = '#E4446E';
const ACCENT = RED;  // weakness page uses red throughout — no blue accents

/* ── Animated number counter (easeOut over 1.5 s) ── */
function AnimNum({ value, prefix = '', suffix = '' }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const dur = 1500;
    let start = null;
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOut cubic
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / dur, 1);
      setDisplay(Math.round(ease(t) * value));
      if (t < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  return <>{prefix}{display}{suffix}</>;
}

// Actionable tips per weakness category (fallback when backend doesn't provide a tip)
const WEAKNESS_TIPS = {
  'time-management': 'Practice with increment games and consciously allocate time per phase.',
  'endgame': 'Study basic endgames (K+P, rook endings) on Lichess practice mode.',
  'opening-prep': 'Pick 2 openings per color and study the first 8-10 moves deeply.',
  'tactical-awareness': 'Solve 10 tactical puzzles daily on Chess.com or Lichess.',
  'positional-play': 'Study master games focusing on pawn structure and piece placement.',
  'consistency': 'Set a daily game limit and stop after 2 consecutive losses.',
  'blunder-rate': 'Before each move, ask: "Does this hang a piece or allow a fork?"',
  'clock-pressure': 'Practice bullet/blitz to build speed, then apply to longer formats.',
  'defense': 'When under attack, look for defensive resources before panicking.',
  'pawn-structure': 'Avoid doubled/isolated pawns unless you get concrete compensation.',
};

export default function WeaknessPage() {
  const { c } = useDestrierTheme();
  const cache = useDataCache();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const outletContext = useOutletContext() || {};
  const displayName = outletContext.profile?.display_name || username;

  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('fetching');

  useEffect(() => {
    if (!username) return;
    const ck = `free-report:${username}:${period}`;
    const hit = cache.get(ck);
    if (hit) { setData(hit); setPhase('ready'); return; }
    setPhase('fetching');
    let cancelled = false;
    fetch(`${API_BASE}/free-report?username=${encodeURIComponent(username)}&period=${period}`)
      .then(r => r.json())
      .then(d => { if (!cancelled) { cache.set(ck, d); setData(d); setPhase('ready'); } })
      .catch(() => { if (!cancelled) setPhase('error'); });
    return () => { cancelled = true; };
  }, [username, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeIdx, setActiveIdx] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [phaseActive, setPhaseActive] = useState(null); // which pie slice is selected
  const [phaseHover, setPhaseHover] = useState(null);
  const [phaseWeaknessIdx, setPhaseWeaknessIdx] = useState(0);
  const highlightIdx = hoveredIdx !== null ? hoveredIdx : activeIdx;

  if (phase === 'fetching') {
    return (
      <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', gap: 3 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', border: `3px solid ${ACCENT}20`, borderTopColor: ACCENT, animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
          <Typography sx={{ color: c.textMuted, fontSize: '1.15rem', fontWeight: 600, textAlign: 'center', maxWidth: 400 }}>
            Analyzing your games — this can take up to a minute for new users.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!data || phase === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <Typography sx={{ color: c.textMuted }}>Could not load weakness data.</Typography>
        </Box>
      </Box>
    );
  }

  // Prefer data-backed Elo cost from the impact analysis (player's own games,
  // performance-rating gap × frequency). Fall back to the severity heuristic
  // for aggregate-only metrics or when sample size was too small to compute.
  // Drop weaknesses where the data says the behavior isn't hurting the player.
  const weaknesses = (data.all_weaknesses || [])
    .map((w) => {
      const impact = w.impact;
      const hasImpact = impact && typeof impact.elo_cost === 'number';
      if (hasImpact && impact.elo_cost <= 0) return null;
      return {
        ...w,
        eloCost: hasImpact ? impact.elo_cost : Math.round((w.severity || 0.5) * 40 + 5),
        dataBacked: !!hasImpact,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.eloCost - a.eloCost)
    .slice(0, 6);

  const maxCost = Math.max(...weaknesses.map(w => w.eloCost), 1);
  weaknesses.forEach(w => { w.radarValue = 0.25 + (w.eloCost / maxCost) * 0.75; });
  const totalEloCost = weaknesses.reduce((s, w) => s + w.eloCost, 0);
  const isCritical = (w) => weaknesses.indexOf(w) < 3;

  // Radar geometry
  const R = 80, CX = 100, CY = 100;
  const angleOf = (i) => (Math.PI * 2 * i) / (weaknesses.length || 1) - Math.PI / 2;
  const radarPoints = weaknesses.map((d, i) => {
    const a = angleOf(i);
    return `${CX + Math.cos(a) * R * d.radarValue},${CY + Math.sin(a) * R * d.radarValue}`;
  }).join(' ');
  const gridPoints = (scale) => weaknesses.map((_, i) => {
    const a = angleOf(i);
    return `${CX + Math.cos(a) * R * scale},${CY + Math.sin(a) * R * scale}`;
  }).join(' ');

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>

      <Box sx={{ pt: { xs: 9, md: 10 }, pb: 6, px: { xs: 2, md: 3, lg: 4 } }}>
        <Box sx={{ position: 'fixed', top: { xs: 56, md: 72 }, left: { xs: 0, md: 0 }, right: { xs: 0, md: '35px' }, display: 'flex', justifyContent: 'center', zIndex: 40, py: 1, pointerEvents: 'none' }}>
          <Box sx={{ pointerEvents: 'auto' }}>
            <TimePeriodFilter value={period} onChange={setPeriod} />
          </Box>
        </Box>
        {/* Title */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
            Weakness Analysis
          </Typography>
          <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
            Hi {displayName},<br />Destrier analyzed your <strong style={{ color: c.textDark }}>{(data.total_games || 0).toLocaleString()} games</strong> and
            found <strong style={{ color: RED }}>{weaknesses.length} main weakness{weaknesses.length !== 1 ? 'es' : ''}</strong>, costing
            you an estimated <strong style={{ color: RED }}>{totalEloCost} Elo</strong> per 100 games.
            Each weakness is ranked by how much rating it costs you — fix the top ones first for the fastest improvement.
            Click any weakness to see details and an actionable tip on how to address it.
          </Typography>
        </Box>

        {/* Total Elo Cost banner */}
        {weaknesses.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1.5, sm: 3 }, mb: 4 }}>
            <Box sx={{
              ...getCardSx(c), p: 3, flexShrink: 0,
              minWidth: 280,
              border: `1.5px solid ${RED}20`,
            }}>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.3 }}>
                Total Elo Cost
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ color: RED, fontSize: '2.5rem', fontWeight: 900, lineHeight: 1, minWidth: '2.5ch', display: 'inline-block' }}>
                  <AnimNum value={totalEloCost} />
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.15rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  elo/100 games
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
                Based on your {weaknesses.length} main weaknesses, you're leaving <strong style={{ color: RED }}>{totalEloCost} rating points</strong> on the table every 100 games.
              </Typography>
              {weaknesses.length >= 2 && (
                <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mt: 0.5 }}>
                  Fix your top 2 weaknesses and you could already recover <strong style={{ color: ACCENT }}>{weaknesses[0].eloCost + weaknesses[1].eloCost} elo</strong>.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {weaknesses.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ color: c.textDark, fontSize: '1.2rem', fontWeight: 700, mb: 1 }}>
              No major weaknesses detected
            </Typography>
            <Typography sx={{ color: c.textMuted }}>
              Keep up the great work, {displayName}! Your game is looking solid.
            </Typography>
          </Box>
        ) : (
          <>
          {/* Section: Weakness Overview */}
          <Typography id="weakness-overview" sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 3, scrollMarginTop: '80px' }}>
            Weakness Overview
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>

            {/* Radar chart */}
            <Box id="radar" sx={{
              flex: 1, minWidth: 0, p: 3, borderRadius: '20px',
              background: `linear-gradient(135deg, ${c.bg} 0%, ${c.cream} 100%)`,
              border: `1px solid ${c.textMuted}15`,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              transition: 'border-color 0.3s ease',
            }}>
              <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1, alignSelf: 'flex-start' }}>
                Weakness Radar
              </Typography>
              <style>{`
                @keyframes radarGrow {
                  from { transform: scale(0); opacity: 0; }
                  to { transform: scale(1); opacity: 1; }
                }
                .weakness-radar-shape {
                  transform-origin: ${CX}px ${CY}px;
                  animation: radarGrow 1.5s ease forwards;
                }
                .weakness-radar-point {
                  opacity: 0;
                  animation: pointFadeIn 1s ease forwards 1.5s;
                }
                @keyframes pointFadeIn {
                  to { opacity: 1; }
                }
              `}</style>
              <svg width="100%" viewBox="0 0 200 200" fill="none" style={{ cursor: 'pointer', overflow: 'visible', maxWidth: 500 }}>
                {[0.25, 0.5, 0.75, 1].map((s) => (
                  <polygon key={s} points={gridPoints(s)} fill="none" stroke={c.textMuted} strokeWidth="0.4" opacity="0.2" />
                ))}
                {weaknesses.map((_, i) => {
                  const a = angleOf(i);
                  return <line key={i} x1={CX} y1={CY} x2={CX + Math.cos(a) * R} y2={CY + Math.sin(a) * R} stroke={c.textMuted} strokeWidth="0.3" opacity="0.2" />;
                })}
                <polygon
                  className="weakness-radar-shape"
                  points={radarPoints}
                  fill={`${highlightIdx !== null && isCritical(weaknesses[highlightIdx]) ? RED : ACCENT}15`}
                  stroke={highlightIdx !== null && isCritical(weaknesses[highlightIdx]) ? RED : ACCENT}
                  strokeWidth="1.8"
                  style={{ transition: 'fill 0.3s, stroke 0.3s' }}
                />
                {weaknesses.map((d, i) => {
                  const a = angleOf(i);
                  const px = CX + Math.cos(a) * R * d.radarValue;
                  const py = CY + Math.sin(a) * R * d.radarValue;
                  const lx = CX + Math.cos(a) * (R + 22);
                  const ly = CY + Math.sin(a) * (R + 22);
                  const isHigh = i === highlightIdx;
                  const critical = isCritical(d);
                  return (
                    <g key={i} className="weakness-radar-point" onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)} onClick={() => setActiveIdx(activeIdx === i ? null : i)} style={{ cursor: 'pointer' }}>
                      <circle cx={px} cy={py} r="18" fill="transparent" />
                      {isHigh && <circle cx={px} cy={py} r={critical ? 10 : 8} fill={critical ? RED : ACCENT} opacity="0.12" />}
                      <circle cx={px} cy={py} r={isHigh ? 5 : 3} fill={critical ? RED : ACCENT} style={{ transition: 'r 0.2s', filter: isHigh ? `drop-shadow(0 0 8px ${critical ? RED : ACCENT}60)` : 'none' }} />
                      {critical && !isHigh && <circle cx={px} cy={py} r="6" fill="none" stroke={RED} strokeWidth="1" opacity="0.35" />}
                      <circle cx={lx} cy={ly} r="16" fill="transparent" />
                      <text x={lx} y={ly} fill={isHigh ? (critical ? RED : ACCENT) : (critical ? RED : ACCENT)} fontSize={isHigh ? '7' : '6'} fontWeight={isHigh || critical ? '700' : '600'} textAnchor="middle" dominantBaseline="middle" opacity={isHigh || critical ? 1 : 0.7} style={{ transition: 'font-size 0.2s, fill 0.2s, opacity 0.2s' }}>
                        {d.label}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </Box>

            {/* Weakness list */}
            <Box id="weakness-list" sx={{ flex: 1, width: '100%' }}>
              <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>
                Weaknesses by Elo Cost
              </Typography>
              {weaknesses.map((w, i) => {
                const isActive = activeIdx === i;
                const isHovered = hoveredIdx === i;
                const wCritical = isCritical(w);
                return (
                  <Box
                    key={w.key || i}
                    onClick={() => setActiveIdx(isActive ? null : i)}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    sx={{
                      mb: 0.8, p: 1.5, px: 2, borderRadius: '14px', cursor: 'pointer',
                      background: isActive ? (wCritical ? '#E4446E10' : `${ACCENT}10`) : isHovered ? (c.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') : 'transparent',
                      border: `1px solid ${isActive ? (wCritical ? '#E4446E30' : `${ACCENT}30`) : 'transparent'}`,
                      transition: 'all 0.2s ease',
                      '&:hover': { background: isActive ? undefined : (c.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{
                          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                          bgcolor: wCritical ? `${RED}15` : `${ACCENT}15`,
                          border: `1.5px solid ${wCritical ? `${RED}40` : `${ACCENT}40`}`,
                          boxShadow: isActive && wCritical ? `0 0 8px ${RED}30` : 'none',
                          transition: 'box-shadow 0.2s',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{ fontSize: '1.05rem', fontWeight: 800, color: c.textDark, lineHeight: 1 }}>{i + 1}</span>
                        </Box>
                        <span style={{ fontSize: '1.05rem', color: isActive ? c.textDark : (wCritical ? c.textDark : ACCENT), fontWeight: isActive ? 700 : 600, opacity: wCritical || isActive ? 1 : 0.8, transition: 'color 0.2s' }}>
                          {w.label}
                        </span>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '1.05rem', color: wCritical ? RED : ACCENT, fontWeight: 700, opacity: wCritical ? 1 : 0.7 }}>
                          -{w.eloCost} elo/100 games
                        </span>
                      </Box>
                    </Box>
                    <Box sx={{ height: 5, borderRadius: 3, background: c.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${(w.eloCost / 50) * 100}%`, borderRadius: 3, background: wCritical ? `linear-gradient(90deg, ${RED}, ${RED}90)` : `linear-gradient(90deg, ${ACCENT}90, ${ACCENT}40)`, transition: 'width 0.6s ease' }} />
                    </Box>
                    <AnimatePresence>
                      {isActive && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: 'hidden' }}>
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${c.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                            <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.6, mb: w.tip ? 0.5 : 0 }}>
                              {w.detail}
                            </Typography>
                            {(w.tip || WEAKNESS_TIPS[w.key]) && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1, p: 1.5, borderRadius: '10px', bgcolor: `${wCritical ? RED : ACCENT}08`, border: `1px solid ${wCritical ? RED : ACCENT}15` }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={wCritical ? RED : ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                                  <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                <Typography sx={{ color: wCritical ? RED : ACCENT, fontSize: '1.05rem', lineHeight: 1.5, fontWeight: 600 }}>
                                  {w.tip || WEAKNESS_TIPS[w.key]}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                );
              })}
            </Box>
          </Box>
          </>
        )}


        {/* ══════════════════════════════════════════════════════════════
            SECTION: Elo Cost by Game Phase — Interactive Pie Chart
           ══════════════════════════════════════════════════════════════ */}
        {weaknesses.length > 0 && (() => {
          const phaseMap = {
            'opening': ['worst_opening', 'narrow_repertoire', 'opening_diversity', 'slow_development', 'early_queen', 'late_castling'],
            'middlegame': ['tactical_awareness', 'pawn_structure', 'center_control', 'move_repetition', 'color_imbalance', 'low_win_rate', 'choking', 'time_trouble', 'tilt_prone'],
            'endgame': ['endgame_conversion', 'endgame', 'high_draw_rate', 'quick_resigner'],
          };
          const phaseLabels = { opening: 'Opening', middlegame: 'Middlegame', endgame: 'Endgame' };
          // Pie chart colors — distinct from red/blue used in weakness list
          const phaseColors = { opening: '#8A74E4', middlegame: '#D46DB1', endgame: '#DE7345' };
          // Top 3 overall weakness keys for red/blue coloring on the right
          const top3Keys = new Set(weaknesses.slice(0, 3).map(w => w.key));
          // Use ALL weaknesses from backend for phase breakdown (not just top 6).
          // Same data-backed Elo cost logic as above.
          const allWeaknesses = (data.all_weaknesses || [])
            .map((w) => {
              const impact = w.impact;
              const hasImpact = impact && typeof impact.elo_cost === 'number';
              if (hasImpact && impact.elo_cost <= 0) return null;
              return {
                ...w,
                eloCost: hasImpact ? impact.elo_cost : Math.round((w.severity || 0.5) * 40 + 5),
                dataBacked: !!hasImpact,
              };
            })
            .filter(Boolean)
            .sort((a, b) => b.eloCost - a.eloCost);

          const phaseCosts = { opening: 0, middlegame: 0, endgame: 0 };
          const phaseWeaknessList = { opening: [], middlegame: [], endgame: [] };
          allWeaknesses.forEach(w => {
            let assigned = false;
            for (const [phase, keys] of Object.entries(phaseMap)) {
              if (keys.some(k => (w.key || '').includes(k))) {
                phaseCosts[phase] += w.eloCost;
                phaseWeaknessList[phase].push(w);
                assigned = true;
                break;
              }
            }
            if (!assigned) {
              phaseCosts.middlegame += w.eloCost;
              phaseWeaknessList.middlegame.push(w);
            }
          });
          // Cap each phase to 7 weaknesses
          Object.keys(phaseWeaknessList).forEach(k => { phaseWeaknessList[k] = phaseWeaknessList[k].slice(0, 7); });
          const phaseTotal = Object.values(phaseCosts).reduce((s, v) => s + v, 0) || 1;
          const phases = [['opening', phaseCosts.opening], ['middlegame', phaseCosts.middlegame], ['endgame', phaseCosts.endgame]];
          const pieOrder = [['middlegame', phaseCosts.middlegame], ['endgame', phaseCosts.endgame], ['opening', phaseCosts.opening]];

          // Pie geometry
          const PIE_R = 130, PIE_CX = 160, PIE_CY = 160;
          const INNER_R = 70; // donut hole
          let cumAngle = -Math.PI / 2; // start at top
          const slices = pieOrder.map(([phase, cost]) => {
            const pct = cost / phaseTotal;
            const startAngle = cumAngle;
            const sweep = pct * Math.PI * 2;
            cumAngle += sweep;
            const endAngle = cumAngle;
            const midAngle = startAngle + sweep / 2;
            return { phase, cost, pct, startAngle, endAngle, midAngle, sweep };
          });

          const arcPath = (cx, cy, r, innerR, startAngle, endAngle) => {
            const largeArc = (endAngle - startAngle) > Math.PI ? 1 : 0;
            const x1 = cx + Math.cos(startAngle) * r;
            const y1 = cy + Math.sin(startAngle) * r;
            const x2 = cx + Math.cos(endAngle) * r;
            const y2 = cy + Math.sin(endAngle) * r;
            const ix1 = cx + Math.cos(endAngle) * innerR;
            const iy1 = cy + Math.sin(endAngle) * innerR;
            const ix2 = cx + Math.cos(startAngle) * innerR;
            const iy2 = cy + Math.sin(startAngle) * innerR;
            return `M${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} L${ix1},${iy1} A${innerR},${innerR} 0 ${largeArc} 0 ${ix2},${iy2} Z`;
          };

          const biggestPhase = [...phases].sort((a, b) => b[1] - a[1])[0]?.[0];
          const highlighted = phaseActive || phaseHover || biggestPhase;

          return (
            <Box sx={{ mt: 6 }}>
              <Typography id="weakness-phase" sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 3, scrollMarginTop: '80px' }}>
                Weaknesses by Game Phase
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>

                {/* Pie chart */}
                <Box sx={{
                  flex: 1, minWidth: 0, p: 3, borderRadius: '20px',
                  background: `linear-gradient(135deg, ${c.bg} 0%, ${c.cream} 100%)`,
                  border: `1px solid ${c.textMuted}15`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}>
                  <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1, alignSelf: 'flex-start' }}>
                    Elo Cost by Phase
                  </Typography>
                  <style>{`
                    @keyframes pieGrow {
                      from { transform: scale(0); opacity: 0; }
                      to { transform: scale(1); opacity: 1; }
                    }
                    @keyframes pieLabelFade {
                      from { opacity: 0; }
                      to { opacity: 1; }
                    }
                    .pie-slices {
                      transform-origin: ${PIE_CX}px ${PIE_CY}px;
                      animation: pieGrow 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    }
                    .pie-label {
                      opacity: 0;
                      animation: pieLabelFade 0.6s ease forwards 0.9s;
                    }
                    .pie-center {
                      opacity: 0;
                      animation: pieLabelFade 0.6s ease forwards 1s;
                    }
                  `}</style>
                  <svg width="100%" viewBox={`0 0 ${PIE_CX * 2} ${PIE_CY * 2}`} style={{ overflow: 'visible', cursor: 'pointer', maxWidth: 500 }}>
                    <g className="pie-slices">
                    {slices.map((s) => {
                      const isHighlighted = phaseHover === s.phase || (!phaseHover && phaseActive === s.phase);
                      const isOther = highlighted && !isHighlighted;
                      const col = phaseColors[s.phase];
                      // Pull highlighted slice outward
                      const pullDist = isHighlighted ? 8 : 0;
                      const pullX = Math.cos(s.midAngle) * pullDist;
                      const pullY = Math.sin(s.midAngle) * pullDist;

                      return (
                        <g key={s.phase}
                          onClick={() => { setPhaseActive(prev => prev === s.phase ? null : s.phase); setPhaseWeaknessIdx(0); }}
                          onMouseEnter={() => setPhaseHover(s.phase)}
                          onMouseLeave={() => setPhaseHover(null)}
                          style={{ cursor: 'pointer', transition: 'transform 0.3s ease' }}
                          transform={`translate(${pullX}, ${pullY})`}
                        >
                          {/* Glow behind highlighted slice */}
                          {isHighlighted && (
                            <path
                              d={arcPath(PIE_CX, PIE_CY, PIE_R + 6, INNER_R - 4, s.startAngle - 0.02, s.endAngle + 0.02)}
                              fill={col}
                              opacity="0.15"
                              style={{ transition: 'opacity 0.3s' }}
                            />
                          )}
                          <path
                            d={arcPath(PIE_CX, PIE_CY, PIE_R, INNER_R, s.startAngle, s.endAngle)}
                            fill={col}
                            opacity={isOther ? 0.35 : 1}
                            style={{ transition: 'opacity 0.2s', filter: isHighlighted ? `drop-shadow(0 0 10px ${col}60)` : 'none' }}
                          />
                          {/* Phase name + percentage labels */}
                          {s.pct > 0.08 && (() => {
                            const midR = (PIE_R + INNER_R) / 2;
                            const tx = PIE_CX + Math.cos(s.midAngle) * midR;
                            const ty = PIE_CY + Math.sin(s.midAngle) * midR;
                            return (
                              <>
                                <text className="pie-label" x={tx} y={ty - 7} fill="#fff" fontSize={isHighlighted ? '13' : '10'} fontWeight="700" textAnchor="middle" dominantBaseline="middle"
                                  style={{ transition: 'font-size 0.2s, opacity 0.25s', pointerEvents: 'none', animationDelay: '0.9s' }}>
                                  {phaseLabels[s.phase]}
                                </text>
                                <text className="pie-label" x={tx} y={ty + 8} fill="#fff" fontSize={isHighlighted ? '11' : '9'} fontWeight="600" textAnchor="middle" dominantBaseline="middle"
                                  style={{ transition: 'font-size 0.2s, opacity 0.25s', pointerEvents: 'none', animationDelay: '0.9s' }}>
                                  {Math.round(s.pct * 100)}%
                                </text>
                              </>
                            );
                          })()}
                        </g>
                      );
                    })}
                    </g>
                    {/* Center label */}
                    <text className="pie-center" x={PIE_CX} y={PIE_CY - 8} fill={c.textDark} fontSize="24" fontWeight="900" textAnchor="middle" dominantBaseline="middle">
                      {phaseTotal}
                    </text>
                    <text className="pie-center" x={PIE_CX} y={PIE_CY + 12} fill={c.textMuted} fontSize="9" fontWeight="600" textAnchor="middle" dominantBaseline="middle">
                      total elo cost
                    </text>
                  </svg>

                  {/* Legend */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    {phases.map(([phase]) => (
                      <Box key={phase}
                        onClick={() => setPhaseActive(prev => prev === phase ? null : phase)}
                        onMouseEnter={() => setPhaseHover(phase)}
                        onMouseLeave={() => setPhaseHover(null)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                          opacity: highlighted && highlighted !== phase ? 0.4 : 1,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: phaseColors[phase], flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: c.textMuted }}>{phaseLabels[phase]}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Weakness list for selected phase */}
                <Box sx={{ flex: 1, width: '100%' }}>
                  {highlighted ? (() => {
                    const phaseWks = phaseWeaknessList[highlighted];
                    const isHoverPhase = phaseHover && phaseHover !== phaseActive;
                    const activeWIdx = isHoverPhase ? 0 : phaseWeaknessIdx;
                    return (
                      <>
                        <div style={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
                          {phaseLabels[highlighted]} Weaknesses — {phaseCosts[highlighted]} elo/100 games
                        </div>
                        {phaseWks.length > 0 ? phaseWks.map((w, wi) => {
                          const isTop3 = top3Keys.has(w.key);
                          const wCol = isTop3 ? RED : ACCENT;
                          const isWActive = activeWIdx === wi;
                          return (
                            <Box key={w.key || wi}
                              onClick={() => setPhaseWeaknessIdx(isWActive ? null : wi)}
                              sx={{
                                mb: 0.8, p: 1.5, px: 2, borderRadius: '14px', cursor: 'pointer',
                                background: isWActive ? `${wCol}10` : 'transparent',
                                border: `1px solid ${isWActive ? `${wCol}30` : 'transparent'}`,
                                transition: 'all 0.2s ease',
                                '&:hover': { background: isWActive ? undefined : (c.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') },
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                  <Box sx={{
                                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: `${wCol}15`, border: `1.5px solid ${wCol}40`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <span style={{ fontSize: '1.05rem', fontWeight: 800, color: c.textDark, lineHeight: 1 }}>{wi + 1}</span>
                                  </Box>
                                  <span style={{ fontSize: '1.05rem', color: isWActive ? c.textDark : (isTop3 ? c.textDark : ACCENT), fontWeight: isWActive ? 700 : 600, opacity: isTop3 || isWActive ? 1 : 0.8, transition: 'color 0.2s' }}>
                                    {w.label}
                                  </span>
                                </Box>
                                <span style={{ fontSize: '1.05rem', color: wCol, fontWeight: 700 }}>-{w.eloCost} elo/100 games</span>
                              </Box>
                              <Box sx={{ height: 5, borderRadius: 3, background: c.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', width: `${(w.eloCost / 50) * 100}%`, borderRadius: 3, background: isTop3 ? `linear-gradient(90deg, ${RED}, ${RED}90)` : `linear-gradient(90deg, ${ACCENT}90, ${ACCENT}40)`, transition: 'width 0.6s ease' }} />
                              </Box>
                              <AnimatePresence>
                                {isWActive && (
                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: 'hidden' }}>
                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${c.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.6, mb: w.tip ? 0.5 : 0 }}>
                                        {w.detail}
                                      </Typography>
                                      {(w.tip || WEAKNESS_TIPS[w.key]) && (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1, p: 1.5, borderRadius: '10px', bgcolor: `${wCol}08`, border: `1px solid ${wCol}15` }}>
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={wCol} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                                            <line x1="12" y1="17" x2="12.01" y2="17" />
                                          </svg>
                                          <Typography sx={{ color: wCol, fontSize: '1.05rem', lineHeight: 1.5, fontWeight: 600 }}>
                                            {w.tip || WEAKNESS_TIPS[w.key]}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </Box>
                          );
                        }) : (
                          <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>No weaknesses found in this phase.</Typography>
                        )}
                      </>
                    );
                  })() : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 500, textAlign: 'center' }}>
                        Click a phase in the chart to see its weaknesses.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })()}


      </Box>
    </Box>
  );
}
